/*
 * myPokemonFilter.ts
 *
 * Pure filtering + sorting for the My Pokémon page — the record-list
 * counterpart to `dexGridFilter.ts` (which filters dex NUMBERS for the grid).
 * That engine's base set is the National Dex roster and its unit is a species;
 * here the base set IS the record list and the unit is one caught Pokémon, so
 * the axes differ where the data does:
 *
 *   - Variant pills narrow by record FLAGS (shiny / lucky / perfect / shadow /
 *     purified / a size read / the Max and Mega axis) and, for the two form
 *     dexes, by the record's own formId (a Galarian Meowth is on Regional, a
 *     party-hat Pikachu on Costume), not by which dex a species appears in.
 *     Every dex the Pokédex page offers is offered here except Complete, the
 *     one that makes no claim about an individual (2026-09-10 owner ask).
 *   - The Dex page's species axes (Extras, Stages, Class, Exclusive) apply to
 *     a record through its species, so the same question ("my starters",
 *     "my region-locked ones") can be asked of the box as of the dex.
 *   - "Caught / Missing" is meaningless (everything here is caught) — sorting
 *     replaces it as the primary lens: recent, CP, IV, dex number, name.
 *   - Search matches the species name, the NICKNAME, and (numeric terms) the
 *     dex number — comma-separated OR terms via the dex page's own
 *     `parseSearchTerms`, so the two search boxes behave identically.
 *
 * Framework-free and pure, like dexGridFilter, so it unit-tests in Node.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import { looksLikeSpeciesMisread } from "./speciesNameMisread";
import { parseSearchTerms } from "./dexGridFilter";
import { bestOptionOf, deriveMyPokemon, nicknameLeagueRanks } from "./myPokemonDerived";
import { bestPotentialFor } from "./bestPotential";
import type {
  ExclusiveFilter,
  FormFilter,
  LineFilter,
  RegionFilter,
} from "./dexGridFilter";
import { regionForId, type PokeType, type Region } from "./types";
import { myPokemonTypes } from "./myPokemonTypes";
import {
  isPerfect,
  ivPercent,
  myPokemonScannedAt,
  type MyPokemonRecord,
} from "./myPokemonStore";
import type { MyPokemonSizeClass } from "./myPokemonStore";
import { heightRatioFor, sizeClassForRatio } from "./speciesSize";
import { formDexVariantFor } from "./myPokemonDexLink";
import { matchesSpeciesClasses, type SpeciesClass } from "./speciesClass";
import { isRegionExclusive } from "./regionExclusive";
import { isBaseOrBabyEvolution } from "./evolutionFamily";

/**
 * The variant pills the My Pokémon page offers, in dex-page order: the
 * scannable ten first, then the extras group (the manual-only and form
 * dexes) in the Pokédex page's own order. Complete is the one dex not here.
 */
export const MY_POKEMON_VARIANTS: readonly PokedexVariant[] = [
  "pokemon",
  "shiny",
  "lucky",
  "perfect",
  "shadow",
  "purified",
  "xxl",
  "xxs",
  "mega",
  "gmax",
  "dynamax",
  "shundo",
  "shinyLucky",
  "regional",
  "costume",
];

/**
 * The Stages axis on a record list. The Dex page's "Whole line" EXPANDS a
 * selection to every family member, which is a grid operation with nothing
 * to expand here (a record is one caught Pokémon); "Base only" is the one
 * that answers a box question ("which of mine are still unevolved").
 */
export const MY_POKEMON_LINE_FILTER_ORDER: readonly LineFilter[] = ["all", "base"];

/**
 * The letter row (2026-09-10 owner ask): a short pill row of A to Z under the
 * dex pills that narrows the list to names starting with that letter, on the
 * same centred-pill mechanic, sized so one flick crosses the alphabet.
 */
export const MY_POKEMON_INITIALS: readonly string[] = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];

/**
 * The letter a record files under: the first letter of the name it displays
 * (nickname first, as the Name sort orders), diacritics stripped so "Éevee"
 * is an E, upper-cased. Null when the name does not start with a letter.
 */
export function myPokemonInitial(r: MyPokemonRecord): string | null {
  const first = myPokemonDisplayName(r)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .charAt(0)
    .toUpperCase();
  return /^[A-Z]$/.test(first) ? first : null;
}

// "gl"/"ul" sort by that league's percent-of-best, with over-cap
// (ineligible) Pokémon sinking to the bottom (2026-08-21 ask). "ml" is the
// Master-league sort: ML rank is a pure stat-product ordering, which overall
// IV tracks, so it orders by IV percent — kept as its OWN key so the League
// buttons and the plain IV chip light up independently (2026-08-25 ask).
// "sizeBig" / "sizeSmall" (2026-09-16 owner ask): GO's size badge as an
// order, XXL first (or XXS first), then within a badge the height against the
// species' own average when both are known. Records with no size read sink.
// "scanned" (2026-09-20 owner ask) is the DEFAULT: the order the Pokémon were
// scanned or screenshotted (record.scannedAt, createdAt for older records),
// so editing a CP no longer jumps a row to the top and loses the trainer's
// place. "recent" is the last-edited order it replaced, kept as "Edited".
export type MyPokemonSort =
  | "scanned"
  | "recent"
  | "cp"
  | "iv"
  | "destiny"
  | "dex"
  | "name"
  | "sizeBig"
  | "sizeSmall"
  | "gl"
  | "ul"
  | "ml"
  // 2026-09-29 owner ask: starred first, max HP, best league rank.
  | "favourite"
  | "hp"
  | "rank"
  // The ring's best option (2026-09-29): what the Best segment sorts by.
  | "best";
/** The generic sort chips. The three league sorts are deliberately NOT here:
 * they render as their own big-button section (2026-08-25 ask — they faded
 * into the other sort options as chips). */
export const MY_POKEMON_SORT_ORDER: readonly MyPokemonSort[] = [
  "scanned",
  "recent",
  "cp",
  "iv",
  "hp",
  "rank",
  "destiny",
  "favourite",
  "dex",
  "name",
  "sizeBig",
  "sizeSmall",
];
export const MY_POKEMON_SORT_LABELS: Record<MyPokemonSort, string> = {
  scanned: "Scanned",
  recent: "Edited",
  cp: "CP",
  iv: "IV %",
  destiny: "Destiny",
  gl: "Great League %",
  ul: "Ultra League %",
  ml: "Master League",
  dex: "Dex #",
  name: "Name",
  sizeBig: "Biggest",
  sizeSmall: "Smallest",
  favourite: "Starred",
  hp: "HP",
  rank: "Rank",
  best: "Best %",
};

/** The four GO size badges as filter chips, smallest to largest. */
export const MY_POKEMON_SIZE_FILTER_ORDER: readonly NonNullable<MyPokemonSizeClass>[] = [
  "xxs",
  "xs",
  "xl",
  "xxl",
];
export const MY_POKEMON_SIZE_LABELS: Record<NonNullable<MyPokemonSizeClass>, string> = {
  xxs: "XXS",
  xs: "XS",
  xl: "XL",
  xxl: "XXL",
};

/** XXL above XL above the unbadged middle above XS above XXS. */
export function sizeClassRank(sizeClass: MyPokemonSizeClass): number {
  switch (sizeClass) {
    case "xxl": return 2;
    case "xl": return 1;
    case "xs": return -1;
    case "xxs": return -2;
    default: return 0;
  }
}

/**
 * The size sort key: the badge's rank, then the height against the species'
 * average (a 1.62x XXL outranks a 1.51x XXL) as a fraction that never crosses
 * a rank boundary. Null when the record carries neither a badge nor a height
 * the species average can place, so it sinks in either direction.
 */
export function sizeSortKey(r: MyPokemonRecord): number | null {
  const ratio = heightRatioFor(r);
  if (r.sizeClass == null && ratio == null) return null;
  const rank = r.sizeClass != null ? sizeClassRank(r.sizeClass) : ratio != null ? sizeClassRank(sizeClassForRatio(ratio)) : 0;
  // Ratios run roughly 0.49 to 2.0; scaled into (-0.5, 0.5) they order
  // within a rank without ever reaching the next one.
  const within = ratio != null ? Math.max(-0.49, Math.min(0.49, (ratio - 1) / 3)) : 0;
  return rank + within;
}
/** The league sort buttons, in cap order, each carrying its ball + sort key. */
export const MY_POKEMON_LEAGUE_SORTS: ReadonlyArray<{
  sort: MyPokemonSort;
  league: "great" | "ultra" | "master";
  label: string;
}> = [
  { sort: "gl", league: "great", label: "Great" },
  { sort: "ul", league: "ultra", label: "Ultra" },
  { sort: "ml", league: "master", label: "Master" },
];

/** Does this individual belong on the given variant pill? */
export function matchesMyPokemonVariant(
  variant: PokedexVariant,
  r: MyPokemonRecord,
): boolean {
  switch (variant) {
    case "pokemon":
      return true;
    case "shiny":
      return r.isShiny;
    case "lucky":
      return r.isLucky;
    case "perfect":
      return isPerfect(r);
    case "shadow":
      return r.isShadow;
    case "purified":
      return r.isPurified;
    case "xxl":
      return r.sizeClass === "xxl";
    case "xxs":
      return r.sizeClass === "xxs";
    case "shundo":
      return r.isShiny && isPerfect(r);
    case "shinyLucky":
      return r.isShiny && r.isLucky;
    // The Max/Mega axis: hand-set on the detail page, never scanned.
    case "mega":
      return r.isMega === true;
    case "gmax":
      return r.isGmax === true;
    case "dynamax":
      return r.isDynamax === true;
    // The form dexes: the record's own form decides, through the same roster
    // the detail page's dex link uses, so a form on neither dex is on neither pill.
    case "regional":
    case "costume":
      return r.speciesId != null && formDexVariantFor(r.speciesId, r.formId) === variant;
    default:
      // Variants with no per-individual meaning (Complete) are never offered;
      // a stray value narrows to nothing rather than lying.
      return false;
  }
}

/** The name a record displays and searches under. */
/**
 * True when two names are the same name to a human: case-, diacritic- and
 * punctuation-insensitive ("Flabebe" == "Flabébé"). Used to stop the detail
 * hero and the list rows printing the species name twice when the OCR'd
 * nickname is just the species name without its accents (2026-08-21).
 */
export function sameLooseName(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const norm = (s: string) =>
    s
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  const na = norm(a);
  return na.length > 0 && na === norm(b);
}

export function myPokemonDisplayName(r: MyPokemonRecord): string {
  return r.nickname ?? r.speciesName ?? (r.speciesId != null ? `#${r.speciesId}` : "Unknown");
}

/**
 * The faint species hint printed beside a nicknamed record's name, or null
 * when it would only repeat the name (2026-09-28 owner report: "when a
 * Pokémon has a name other than its original it sometimes repeats twice").
 *
 * No hint when:
 *   - there is no nickname, or no species to hint;
 *   - the nickname IS the species name loosely (accents, case, punctuation:
 *     "Flabebe" vs "Flabébé", 2026-08-21);
 *   - the nickname is the species name MISREAD by the scanner (a cut or a
 *     one-glyph slip: "Marowa", "MAROWAK."), see lib/speciesNameMisread;
 *   - the nickname already CONTAINS the species name ("Marowak 91 ✨", the
 *     naming template's output), so the hint would print it a second time.
 */
export function myPokemonSubName(r: Pick<MyPokemonRecord, "nickname" | "speciesName">): string | null {
  const nickname = r.nickname?.trim();
  const species = r.speciesName?.trim();
  if (!nickname || !species) return null;
  if (sameLooseName(nickname, species)) return null;
  if (looksLikeSpeciesMisread(nickname, species)) return null;
  if (looseKey(nickname).includes(looseKey(species))) return null;
  return species;
}

function looseKey(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function matchesSearchTerm(r: MyPokemonRecord, term: string): boolean {
  const numeric = term.match(/^#?0*(\d{1,4})$/);
  if (numeric) {
    // Numeric terms are an EXACT dex-number match, same as the dex page's
    // search (deliberately not substring, so "1,4,7" can't pull in #10-19).
    return r.speciesId === Number(numeric[1]);
  }
  const hay = [r.speciesName ?? "", r.nickname ?? ""].join(" ").toLowerCase();
  return hay.includes(term);
}

function matchesSearch(r: MyPokemonRecord, terms: string[]): boolean {
  if (terms.length === 0) return true;
  return terms.some((t) => matchesSearchTerm(r, t));
}

function matchesRegion(r: MyPokemonRecord, region: RegionFilter): boolean {
  if (region === "all") return true;
  if (r.speciesId == null) return false;
  return regionForId(r.speciesId) === (region as Region);
}

function matchesTypes(r: MyPokemonRecord, types: PokeType[]): boolean {
  if (types.length === 0) return true;
  // The species table decides (myPokemonTypes), never a scan.
  const own = myPokemonTypes(r);
  return types.some((t) => own.includes(t));
}

/**
 * Extras on a record: "Forms" is a record that IS a form (regional or any
 * other canonical form, or an import's own form label), "Costume" one that
 * wears a costume. Species-level ("has forms") would light up every plain
 * Pikachu on the Costume chip, which is not what a box question means.
 */
function matchesForms(r: MyPokemonRecord, forms: FormFilter): boolean {
  if (forms === "all") return true;
  const dexVariant = r.speciesId != null ? formDexVariantFor(r.speciesId, r.formId) : null;
  if (forms === "costumes") return dexVariant === "costume";
  return dexVariant !== "costume" && (r.formId != null || r.form != null);
}

function matchesClasses(r: MyPokemonRecord, classes: readonly SpeciesClass[]): boolean {
  if (classes.length === 0) return true;
  return r.speciesId != null && matchesSpeciesClasses(classes, r.speciesId);
}

function matchesExclusive(r: MyPokemonRecord, exclusive: ExclusiveFilter): boolean {
  if (exclusive === "all") return true;
  if (r.speciesId == null) return false;
  return isRegionExclusive(r.speciesId) === (exclusive === "exclusive");
}

function matchesLine(r: MyPokemonRecord, line: LineFilter): boolean {
  if (line !== "base") return true;
  return r.speciesId != null && isBaseOrBabyEvolution(r.speciesId);
}

export interface MyPokemonFilterOptions {
  records: MyPokemonRecord[];
  variant: PokedexVariant;
  /** Raw search text; comma-separated OR terms (parseSearchTerms). */
  search: string;
  region: RegionFilter;
  types: PokeType[];
  sort: MyPokemonSort;
  /** The Dex page's species axes; each defaults to its off state. */
  forms?: FormFilter;
  /** Keep only starred records (2026-09-29 owner ask: Starred in Extras). */
  starred?: boolean;
  classes?: readonly SpeciesClass[];
  exclusive?: ExclusiveFilter;
  line?: LineFilter;
  /** A letter from MY_POKEMON_INITIALS, or null for every name. */
  initial?: string | null;
  /**
   * The second sort level (2026-09-16 owner ask): ties on `sort` break on
   * this, then on recency as always. Ignored when it equals `sort`.
   */
  sortThen?: MyPokemonSort | null;
  /**
   * Run the primary level the other way (2026-09-29 owner ask: a second
   * tap on the Best / IV% segment sorts by it high to low, a third low to
   * high). Nulls still sink whichever way it runs.
   */
  reverse?: boolean;
  /** GO size badges to keep; empty keeps every record. */
  sizes?: readonly MyPokemonSizeClass[];
  /** Reads that must be BLANK on a kept record (2026-09-20: "show me every
   * Pokémon with no CP"); empty keeps every record. Several chips narrow
   * TOGETHER: a record must be missing every one of them (owner direction,
   * same day: "selecting all 3 gives the entries missing all 3"). */
  missing?: readonly MyPokemonMissingRead[];
}

/** A read the Reads row can ask to be blank. */
export type MyPokemonMissingRead = "cp" | "ivs" | "moves" | "size";
export const MY_POKEMON_MISSING_ORDER: readonly MyPokemonMissingRead[] = ["cp", "ivs", "moves", "size"];
export const MY_POKEMON_MISSING_LABELS: Record<MyPokemonMissingRead, string> = {
  cp: "No CP",
  ivs: "No IVs",
  moves: "No moves",
  size: "No size",
};

export function isMissingRead(r: MyPokemonRecord, read: MyPokemonMissingRead): boolean {
  switch (read) {
    case "cp":
      return r.cp == null;
    case "ivs":
      return r.ivAtk == null || r.ivDef == null || r.ivHp == null;
    case "moves":
      return r.moves.length === 0;
    case "size":
      return r.heightM == null;
  }
}

type SortKey = number | string | null;
interface SortSpec {
  key: (r: MyPokemonRecord) => SortKey;
  dir: "asc" | "desc";
}

/**
 * One sort level as a key function plus a direction. Every key is computed
 * exactly once per record by the caller (decorate-sort-undecorate; see the
 * perf note in filterMyPokemon), and null ALWAYS sinks to the bottom
 * whichever way the level runs.
 */
function sortSpecFor(sort: MyPokemonSort): SortSpec {
  switch (sort) {
    case "cp":
      return { key: (r) => r.cp, dir: "desc" };
    case "iv":
    // ML rank is a pure stat-product ordering with no cap, which overall IV
    // percent tracks — so the Master button sorts exactly like IV %.
    case "ml":
      return { key: (r) => ivPercent(r), dir: "desc" };
    case "dex":
      return { key: (r) => r.speciesId, dir: "asc" };
    case "name":
      return { key: (r) => myPokemonDisplayName(r), dir: "asc" };
    // Destiny (2026-08-31 ask): the best full PvP rank this exact Pokémon
    // can reach across its WHOLE evolution line (bestPotential's sweep, the
    // same number the "Named for its destiny" template prints). Rank 1 is
    // the best of 4096, so the key inverts it for the descending helper;
    // equal ranks fall back to the destiny percent, then recency. Records
    // without IVs have no destiny and sink to the bottom.
    case "destiny":
      return {
        key: (r) => {
          const p = bestPotentialFor(r, deriveMyPokemon(r).level);
          return p ? 4097 - p.rank + (p.percent ?? 0) / 1000 : null;
        },
        dir: "desc",
      };
    // "gl"/"ul" sort by that league's percent-of-best, with over-cap
    // (ineligible) Pokémon sinking to the bottom (2026-08-21 ask).
    case "gl":
      return {
        key: (r) => {
          const d = deriveMyPokemon(r);
          return d.greatEligible ? d.greatPct : null;
        },
        dir: "desc",
      };
    case "ul":
      return {
        key: (r) => {
          const d = deriveMyPokemon(r);
          return d.ultraEligible ? d.ultraPct : null;
        },
        dir: "desc",
      };
    // Starred first (2026-09-29): the star is a boolean, so ties (every
    // starred one, every plain one) fall through to the next level, and
    // the default second level is scanned order.
    case "favourite":
      return { key: (r) => (r.isFavourite ? 1 : 0), dir: "desc" };
    case "hp":
      return { key: (r) => r.hpMax, dir: "desc" };
    // Rank (2026-09-29): this exact Pokémon's best FULL league rank across
    // Great and Ultra (1 of 4096 is best), over-cap leagues excluded, so
    // the top spreads lead. No IVs, no rank: sinks to the bottom.
    case "rank":
      return {
        key: (r) => {
          const { greatRank, ultraRank } = nicknameLeagueRanks(r);
          const ranks = [greatRank, ultraRank].filter((x): x is number => x != null);
          return ranks.length > 0 ? Math.min(...ranks) : null;
        },
        dir: "asc",
      };
    // The ring's reading in Best mode: the best option's percent (a league
    // percent when a league won, else plain IVs), the 2026-08-30 rule.
    case "best":
      return { key: (r) => bestOptionOf(deriveMyPokemon(r))?.score ?? null, dir: "desc" };
    case "sizeBig":
      return { key: sizeSortKey, dir: "desc" };
    case "sizeSmall":
      return { key: sizeSortKey, dir: "asc" };
    case "recent":
      return { key: (r) => r.updatedAt, dir: "desc" };
    case "scanned":
    default:
      return { key: (r) => myPokemonScannedAt(r), dir: "desc" };
  }
}

function compareSortKeys(a: SortKey, b: SortKey, dir: "asc" | "desc"): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "string" || typeof b === "string") {
    const c = String(a).localeCompare(String(b));
    return dir === "asc" ? c : -c;
  }
  return dir === "asc" ? a - b : b - a;
}

export function filterMyPokemon(opts: MyPokemonFilterOptions): MyPokemonRecord[] {
  const terms = parseSearchTerms(opts.search);
  const forms = opts.forms ?? "all";
  const classes = opts.classes ?? [];
  const exclusive = opts.exclusive ?? "all";
  const line = opts.line ?? "all";
  const initial = opts.initial ?? null;
  const sizes = opts.sizes ?? [];
  const missing = opts.missing ?? [];
  const starred = opts.starred ?? false;
  const filtered = opts.records.filter(
    (r) =>
      (!starred || !!r.isFavourite) &&
      matchesMyPokemonVariant(opts.variant, r) &&
      matchesSearch(r, terms) &&
      matchesRegion(r, opts.region) &&
      matchesTypes(r, opts.types) &&
      matchesForms(r, forms) &&
      matchesClasses(r, classes) &&
      matchesExclusive(r, exclusive) &&
      matchesLine(r, line) &&
      (sizes.length === 0 || sizes.includes(r.sizeClass)) &&
      missing.every((m) => isMissingRead(r, m)) &&
      (initial == null || myPokemonInitial(r) === initial),
  );
  const byRecent = (a: MyPokemonRecord, b: MyPokemonRecord) =>
    myPokemonScannedAt(b) - myPokemonScannedAt(a) || b.updatedAt - a.updatedAt;
  // Decorate-sort-undecorate: the comparator runs O(n·log n) times, so
  // computing a key IN the comparator multiplied the per-record work ~12x at
  // 3,000 records — and for GL/UL the per-record work was a full league
  // derivation, which froze the page on sort (2026-08-21 device report).
  // Every level's key is computed exactly once per record here.
  //
  // TWO LEVELS (2026-09-16 owner ask): the primary sort, then `sortThen`
  // where the primary ties, then recency as the constant last word.
  const primary = sortSpecFor(opts.sort);
  const primaryDir: "asc" | "desc" = opts.reverse ? (primary.dir === "asc" ? "desc" : "asc") : primary.dir;
  const secondary =
    opts.sortThen && opts.sortThen !== opts.sort ? sortSpecFor(opts.sortThen) : null;
  const keyed = filtered.map((r) => ({
    r,
    k1: primary.key(r),
    k2: secondary ? secondary.key(r) : null,
  }));
  keyed.sort(
    (a, b) =>
      compareSortKeys(a.k1, b.k1, primaryDir) ||
      (secondary ? compareSortKeys(a.k2, b.k2, secondary.dir) : 0) ||
      byRecent(a.r, b.r),
  );
  return keyed.map((k) => k.r);
}
